import { useState, useRef } from 'react';
import './image-upload.scss';

interface ImageUploadProps {
  onImagesUploaded: (files: File[]) => void | Promise<void>;
  maxImages?: number;
  language?: 'ar' | 'en' | 'ur';
  acceptVideo?: boolean;
}

export default function ImageUpload({ onImagesUploaded, maxImages = 10, language = 'ar', acceptVideo = true }: ImageUploadProps) {
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    if (uploadedFiles.length + files.length > maxImages) {
      const message = language === 'ar'
        ? `يمكنك رفع ${maxImages} صور كحد أقصى`
        : language === 'ur'
          ? `آپ زیادہ سے زیادہ ${maxImages} تصاویر اپ لوڈ کر سکتے ہیں`
          : `You can upload a maximum of ${maxImages} images`;
      alert(message);
      return;
    }

    const newFiles = [...uploadedFiles, ...files];
    setUploadedFiles(newFiles);

    // Generate previews
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setPreviews([...previews, ...newPreviews]);

    // Notify parent
    onImagesUploaded(newFiles);
  };

  const removeImage = (index: number) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);

    // Revoke URL to free memory
    URL.revokeObjectURL(previews[index]);

    setUploadedFiles(newFiles);
    setPreviews(newPreviews);
    onImagesUploaded(newFiles);
  };

  const texts = {
    ar: {
      title: 'رفع صور/فيديو الحادث',
      button: 'اختر صور أو فيديو'
    },
    en: {
      title: 'Upload Accident Photos / Videos',
      button: 'Choose Photos or Videos'
    },
    ur: {
      title: 'حادثے کی تصاویر/ویڈیو اپ لوڈ کریں',
      button: 'تصاویر یا ویڈیو منتخب کریں'
    }
  };

  const t = texts[language];

  return (
    <div className="image-upload-container">
      <div className="upload-header">
        <h3>{t.title}</h3>
        <p className="upload-count">{uploadedFiles.length} / {maxImages}</p>
      </div>

      <button
        className="upload-button"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploadedFiles.length >= maxImages}
      >
        <span className="material-symbols-outlined">add_photo_alternate</span>
        {t.button}
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept={acceptVideo ? "image/*,video/*" : "image/*"}
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {previews.length > 0 && (
        <div className="preview-grid">
          {previews.map((preview, index) => (
            <div key={index} className="preview-item">
              <img src={preview} alt={`Preview ${index + 1}`} />
              <button
                className="remove-button"
                onClick={() => removeImage(index)}
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

